/**
 * Quotation money helpers — always calculate/store with 2 decimal places.
 */

export const roundMoney = (value) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) {
        return 0;
    }
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const formatMoney = (value) => {
    return roundMoney(value).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const moneyTotal = (adults, adultPrice, childs = 0, childPrice = 0, infants = 0, infantPrice = 0) => {
    return roundMoney(
        (parseFloat(adults) || 0) * roundMoney(adultPrice)
        + (parseFloat(childs) || 0) * roundMoney(childPrice)
        + (parseFloat(infants) || 0) * roundMoney(infantPrice)
    );
};

/** Max booking discount as a fraction of quote price (5%). */
export const MAX_DISCOUNT_PERCENT = 5;

/**
 * Resolve booking discount (amount or percent), capped at 5% of quote.
 * @returns {{ discount_amount: number, scale: number, effective_quote: number, max_amount: number, error: string|null }}
 */
export const resolveDiscount = ({ quotePrice, type, value }) => {
    const quote = roundMoney(quotePrice);
    const rawValue = Math.max(0, roundMoney(value));
    const discountType = type === 'amount' ? 'amount' : 'percent';
    const maxAmount = roundMoney(quote * MAX_DISCOUNT_PERCENT / 100);

    let discountAmount = 0;
    if (discountType === 'percent') {
        discountAmount = roundMoney(quote * rawValue / 100);
    } else {
        discountAmount = rawValue;
    }

    let error = null;
    if (discountAmount > maxAmount + 0.001) {
        error = `Discount cannot exceed ${MAX_DISCOUNT_PERCENT}% of quote price (max ₹${maxAmount.toLocaleString('en-IN')}).`;
        discountAmount = maxAmount;
    }

    if (quote <= 0) {
        return {
            discount_amount: 0,
            scale: 1,
            effective_quote: 0,
            max_amount: 0,
            error: null,
        };
    }

    const effectiveQuote = roundMoney(Math.max(0, quote - discountAmount));
    const scale = effectiveQuote / quote;

    return {
        discount_amount: discountAmount,
        scale,
        effective_quote: effectiveQuote,
        max_amount: maxAmount,
        error,
    };
};

/**
 * Resolve a quotation discount (percent or amount) against the gross quote.
 * This does not apply the booking 5% cap. An invalid value is not applied.
 * @returns {{ discount_type: string, discount_value: number, discount_amount: number, payable: number, error: string|null }}
 */
export const resolveQuotationDiscount = ({ quotePrice, type, value }) => {
    const quote = roundMoney(quotePrice);
    const discountType = type === 'amount' ? 'amount' : 'percent';
    const empty = {
        discount_type: discountType,
        discount_value: 0,
        discount_amount: 0,
        payable: quote,
        error: null,
    };

    if (value === '' || value === null || value === undefined) {
        return empty;
    }

    const numeric = parseFloat(value);
    if (Number.isNaN(numeric)) {
        return {
            ...empty,
            error: 'Enter a valid discount.',
        };
    }

    if (numeric < 0) {
        return {
            ...empty,
            discount_value: roundMoney(numeric),
            error: 'Discount cannot be negative.',
        };
    }

    const rawValue = roundMoney(numeric);
    const discountAmount = discountType === 'percent'
        ? roundMoney(quote * rawValue / 100)
        : rawValue;

    let error = null;
    if (discountType === 'percent' && rawValue > 100) {
        error = 'Discount percent cannot exceed 100.';
    } else if (quote <= 0 && rawValue > 0) {
        error = 'Enter a land package amount before applying a discount.';
    } else if (discountAmount > quote + 0.001) {
        error = 'Discount cannot exceed the land package before GST.';
    }

    if (error) {
        return {
            discount_type: discountType,
            discount_value: rawValue,
            discount_amount: 0,
            payable: quote,
            error,
        };
    }

    return {
        discount_type: discountType,
        discount_value: rawValue,
        discount_amount: discountAmount,
        payable: roundMoney(Math.max(0, quote - discountAmount)),
        error: null,
    };
};
